/**
 * Touch gestures of the viewers: the double tap that leaves immersive
 * (full-screen) mode in all three, the pan / pinch arithmetic the image
 * viewer and the live-session video panel share, and the pan gesture state
 * machine that only the video panel runs (see PanGesture).
 *
 * Pure functions, because the inline versions kept breaking on devices nobody
 * could test from a desk: a click-based pointer check left iPhones stuck in
 * immersive mode (25. 9. 2026), and a single tap toggled the mode so easily
 * that a pinch in the image viewer "sometimes caught it" (MU, 25. 9. 2026).
 * Since the same meeting the mode is ENTERED only with the header button (or
 * F); a double tap only leaves it, so it can never collide with pan and zoom.
 */
/** Longest gap between the two taps of a double tap. */
export const DOUBLE_TAP_MS = 300;
/** Furthest the second tap of a double tap may land from the first. */
export const DOUBLE_TAP_SLOP_PX = 30;
/**
 * How far a finger may travel and still be a tap. Past this the press is a
 * pan or a scroll, and the click it may end with is not a tap.
 */
export const TAP_MOVE_SLOP_PX = 10;
export const IDLE_TAP_GESTURE = {
    pointers: {},
    downPointerType: undefined,
    disqualified: false,
};
export function tapGestureDown(g, p) {
    const others = Object.keys(g.pointers).filter((id) => Number(id) !== p.pointerId);
    // A lone pointer starts a new gesture. Re-seeing an id that is already down
    // means its `pointerup` never reached us (a mouse released outside the
    // window), so that is a fresh start too, not a second finger.
    //
    // So does a primary pointer. Touch pointerIds never repeat, so a finger
    // whose `pointerup` / `pointercancel` was lost (the WebView was backgrounded
    // mid-press, a system gesture took the touch) would otherwise stay "down"
    // for good, every later tap would count as a second finger, and a reader in
    // immersive mode could never double-tap back out. The browser marks a
    // pointer primary only when no other pointer of its type is down, so any
    // finger still in `pointers` at that moment is a stale leftover.
    if (p.isPrimary || others.length === 0) {
        return {
            pointers: { [p.pointerId]: { x: p.x, y: p.y } },
            downPointerType: p.pointerType,
            disqualified: false,
        };
    }
    // A second finger: this is a pinch or a two-finger pan, never a tap.
    return {
        ...g,
        pointers: { ...g.pointers, [p.pointerId]: { x: p.x, y: p.y } },
        disqualified: true,
    };
}
export function tapGestureMove(g, p) {
    const start = g.pointers[p.pointerId];
    // A hovering mouse moves with no button down; it is not part of a press.
    if (!start || g.disqualified)
        return g;
    if (Math.hypot(p.x - start.x, p.y - start.y) <= TAP_MOVE_SLOP_PX)
        return g;
    return { ...g, disqualified: true };
}
/**
 * A pointer lifted (`cancelled: false`) or was taken over by the browser for a
 * scroll or a native zoom (`cancelled: true`). The gesture outlives its last
 * pointer: the `click` it ends with arrives after the `pointerup`.
 */
export function tapGestureUp(g, pointerId, cancelled) {
    if (!(pointerId in g.pointers))
        return g;
    const pointers = { ...g.pointers };
    delete pointers[pointerId];
    return { ...g, pointers, disqualified: g.disqualified || cancelled };
}
/** The click has read the gesture; the next click must not reuse it. */
export function consumeTapGesture(g) {
    return Object.keys(g.pointers).length > 0 ? g : IDLE_TAP_GESTURE;
}
/**
 * May a tap from this pointer count towards leaving immersive mode at all?
 *
 * The pointer type must come from the `pointerdown` that started the tap, not
 * from the `click`. A click-based check shipped on 24. 9. 2026, and on an
 * iPhone MU found the tap dead in both transcript viewers (25. 9. 2026): iOS
 * WebKit does not report a finger tap's synthesized `click` as `'touch'`. The
 * `pointerdown` that starts the tap does.
 *
 * Any pointer on a device with a touchscreen counts, because an iPad with a
 * trackpad clicks as a mouse and has nothing else on screen to press. A
 * mouse-only desktop keeps its clicks (focusing the text, selecting a word)
 * and leaves with Escape, F or the header button. (Entering by double tap was
 * touch-only; since MU 25. 9. 2026 a double tap never enters.)
 */
export function isImmersiveTapPointer(s) {
    return s.downPointerType !== 'mouse' || s.anyCoarsePointer;
}
function isSecondTap(first, second) {
    const gap = second.time - first.time;
    if (gap < 0 || gap > DOUBLE_TAP_MS)
        return false;
    return Math.hypot(second.x - first.x, second.y - first.y) <= DOUBLE_TAP_SLOP_PX;
}
/**
 * Should this tap leave immersive mode? Only the second tap of a double tap
 * in the middle of the content does, and only while immersive: a double tap
 * never ENTERS the mode (agreed with MU on 25. 9. 2026). Entering is the
 * header button's job (or F), so a double tap meant for zooming or panning
 * the content can never blow it up to full screen. A single tap toggled
 * before, and it fired on taps meant for something else: resting a finger to
 * read, or the end of a pinch.
 *
 * With VoiceOver on, its own double tap reaches the page as ONE click, so it
 * never completes this double tap. That is deliberate — VoiceOver users have
 * the labelled header button, which is where VoiceOver leads them anyway.
 */
export function decideImmersiveTap(s) {
    if (!s.immersive || s.ignored || !isImmersiveTapPointer(s)) {
        return { exit: false, pendingTap: undefined };
    }
    // The second tap may find text selected by the double click itself (an
    // iPad trackpad selects a word), so only the FIRST tap checks for it.
    if (s.pendingTap && isSecondTap(s.pendingTap, s.tap)) {
        return { exit: true, pendingTap: undefined };
    }
    if (s.hasSelection)
        return { exit: false, pendingTap: undefined };
    return { exit: false, pendingTap: s.tap };
}
/**
 * Keep the zoomed picture covering its pane. The picture is scaled about the
 * pane centre, so along each axis it may travel half of the amount by which
 * the scaled picture is bigger than the pane — any further and it would pull
 * away from the opposite edge. Along an axis where the scaled picture is not
 * bigger than the pane there is nothing to reveal, and it stays centred.
 *
 * `content` is the picture as drawn at 100 %, centred in the pane. It
 * defaults to the whole pane (the image viewer's picture box). A letterboxed
 * `object-contain` video is smaller than its pane along one axis; clamping by
 * the pane there would let the reader pan into the black bars.
 */
export function clampPanOffset(offset, zoom, pane, content = pane) {
    const maxX = Math.max(0, (content.width * zoom - pane.width) / 2);
    const maxY = Math.max(0, (content.height * zoom - pane.height) / 2);
    return {
        // `+ 0` turns the -0 that clamping 0 against -0 yields into 0.
        x: Math.max(-maxX, Math.min(maxX, offset.x)) + 0,
        y: Math.max(-maxY, Math.min(maxY, offset.y)) + 0,
    };
}
/**
 * The box an `object-fit: contain` picture of `intrinsic` size is drawn in
 * inside `box`: as big as fits without cropping, aspect ratio kept. An
 * unknown intrinsic size (a video before its first frame) fills the box.
 */
export function containedSize(intrinsic, box) {
    if (intrinsic.width <= 0 || intrinsic.height <= 0)
        return { width: box.width, height: box.height };
    const scale = Math.min(box.width / intrinsic.width, box.height / intrinsic.height);
    return { width: intrinsic.width * scale, height: intrinsic.height * scale };
}
/**
 * The pan offset that keeps the picture under the fingers during a pinch.
 *
 * The pinch used to scale about the pane centre and ignore where the fingers
 * went, so moving two fingers did not move the picture at all and the pan
 * seemed to "move terribly little" (MU, 25. 9. 2026). Now the point that was
 * under the fingers' midpoint stays under it: moving both fingers pans 1:1,
 * and spreading them zooms into the spot between them.
 *
 * With `translate(pan) scale(zoom)` about the pane centre, a picture point `q`
 * is drawn at `pan + zoom * q`. Solving `mid = pan' + zoom' * q` for the `q`
 * that was at `start.mid` gives the result (before clamping).
 */
export function pinchPanOffset(start, mid, zoom) {
    const ratio = zoom / start.zoom;
    return {
        x: mid.x - ratio * (start.mid.x - start.pan.x),
        y: mid.y - ratio * (start.mid.y - start.pan.y),
    };
}
/**
 * Does the zoomed picture own touch gestures on its pane, or does the browser?
 *
 * Above 100 % the picture does: one finger pans it and two fingers pan and
 * pinch it, so the pane must be `touch-action: none` or the browser would
 * scroll or zoom the page under the fingers and cancel the gesture half way.
 *
 * At or below 100 % there is nothing to pan, and the browser does: a page
 * that cannot be scrolled or pinch-zoomed from a picture that fills most of a
 * phone screen is an accessibility regression for the readers this viewer is
 * for. Tying the two-finger pinch to `touch-action` of the moment is not an
 * option, because the browser reads `touch-action` when the first finger
 * lands and not again for the second, so a pinch at 100 % zooms the page
 * natively and the zoom buttons (or the wheel) zoom the picture.
 */
export function zoomedPictureOwnsTouch(zoom) {
    return zoom > 1;
}
export const IDLE_PAN_GESTURE = { kind: 'idle' };
/** Zoom on whole tenths within the limits: 0.1 steps drift in binary. */
export function clampZoomLevel(zoom, limits) {
    return Math.round(Math.max(limits.minZoom, Math.min(limits.maxZoom, zoom)) * 10) / 10;
}
function midpointFromPaneCentre(a, b, pane) {
    return {
        x: (a.x + b.x) / 2 - (pane.left + pane.width / 2),
        y: (a.y + b.y) / 2 - (pane.top + pane.height / 2),
    };
}
/**
 * Start a gesture from the points now down. Call it on every `touchstart` and
 * on every `touchend` / `touchcancel` with the points still down, not only the
 * first time: a pinch whose second finger lifts carries on as a drag with the
 * finger that is left, and a drag that gains a finger becomes a pinch, so the
 * picture never jumps.
 *
 * Nothing starts at or below 100 %: the browser owns the touch there (see
 * zoomedPictureOwnsTouch). A pinch that began above 100 % and zooms out below
 * it carries on, because it started while the picture owned the touch.
 */
export function beginPanGesture(points, view, pane) {
    if (!zoomedPictureOwnsTouch(view.zoom))
        return IDLE_PAN_GESTURE;
    if (points.length === 1) {
        return { kind: 'drag', start: { x: points[0].x, y: points[0].y }, origin: view.pan };
    }
    if (points.length === 2) {
        const [a, b] = points;
        return {
            kind: 'pinch',
            distance: Math.hypot(a.x - b.x, a.y - b.y),
            start: { zoom: view.zoom, pan: view.pan, mid: midpointFromPaneCentre(a, b, pane) },
        };
    }
    return IDLE_PAN_GESTURE;
}
/**
 * The view after the points moved, or `null` when the move is not part of the
 * gesture (no gesture, or a different number of points than it started with).
 *
 * A drag moves the picture exactly as far as the finger went at the CURRENT
 * zoom, so a wheel or button zoom during a drag sticks. A pinch keeps the spot
 * between the fingers under them (see pinchPanOffset) and is the only gesture
 * that changes the zoom. Both are clamped so the picture always covers its
 * pane.
 */
export function movePanGesture(g, points, frame) {
    const { pane, content, limits } = frame;
    if (g.kind === 'drag' && points.length === 1) {
        const pan = {
            x: g.origin.x + (points[0].x - g.start.x),
            y: g.origin.y + (points[0].y - g.start.y),
        };
        return { zoom: frame.zoom, pan: clampPanOffset(pan, frame.zoom, pane, content) };
    }
    if (g.kind === 'pinch' && points.length === 2) {
        const [a, b] = points;
        const spread = Math.hypot(a.x - b.x, a.y - b.y);
        const zoom = limits.zoomLocked || g.distance === 0
            ? g.start.zoom
            : clampZoomLevel((g.start.zoom * spread) / g.distance, limits);
        const pan = pinchPanOffset(g.start, midpointFromPaneCentre(a, b, pane), zoom);
        return { zoom, pan: clampPanOffset(pan, zoom, pane, content) };
    }
    return null;
}
/** How far one arrow-key press pans, as a fraction of the pane. */
export const KEYBOARD_PAN_FRACTION = 0.1;
/**
 * The pan after an arrow key, or `null` when the key does not pan (not an
 * arrow, or the picture is at or below 100 % and has nowhere to go) so the
 * caller leaves the key alone.
 *
 * An arrow moves the VIEW towards that edge, the way scrolling does:
 * ArrowRight reveals more of the right side, so the picture moves left.
 */
export function keyboardPanOffset(key, view, pane, content = pane) {
    if (view.zoom <= 1)
        return null;
    const stepX = pane.width * KEYBOARD_PAN_FRACTION;
    const stepY = pane.height * KEYBOARD_PAN_FRACTION;
    let d;
    switch (key) {
        case 'ArrowLeft':
            d = { x: stepX, y: 0 };
            break;
        case 'ArrowRight':
            d = { x: -stepX, y: 0 };
            break;
        case 'ArrowUp':
            d = { x: 0, y: stepY };
            break;
        case 'ArrowDown':
            d = { x: 0, y: -stepY };
            break;
        default:
            return null;
    }
    return clampPanOffset({ x: view.pan.x + d.x, y: view.pan.y + d.y }, view.zoom, pane, content);
}
/**
 * Whether the focused element uses the arrow keys itself: a text field moves
 * its caret, a select changes its option, a range input its value, an
 * editable region its caret. A page-wide pan shortcut must leave those keys
 * alone. Buttons and links do nothing with arrows, so a reader who has just
 * pressed the zoom button can pan straight away with focus still on it.
 */
export function keyTargetOwnsArrows(target) {
    if (!target)
        return false;
    if (target.isContentEditable)
        return true;
    const tag = target.tagName.toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
/**
 * The pan after a page-wide keydown, or `null` when the key is not a pan and
 * must be left alone (no preventDefault): a modifier chord (browser and OS
 * shortcuts), a key aimed at a field that uses arrows itself, or anything
 * keyboardPanOffset refuses — not an arrow, or a picture at or below 100 %.
 */
export function keyboardPanForKeyDown(k, view, pane, content = pane) {
    if (k.altKey || k.ctrlKey || k.metaKey)
        return null;
    if (keyTargetOwnsArrows(k.target))
        return null;
    return keyboardPanOffset(k.key, view, pane, content);
}
//# sourceMappingURL=viewer-gestures.js.map